import React, { useEffect, useState, Fragment } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { getJobs, getCountries, getImageUrl } from '../services/api';
import AOS from 'aos';
import 'aos/dist/aos.css';
import './Home.css';
import '../components/Filters.css'; // Import the new CSS
import Flag from 'react-flagpack';
import 'react-flagpack/dist/style.css';

const getCountryFlagCode = (code) => {
  if (!code) return null;
  const lowerCode = code.toLowerCase();

  // Special name-to-code overrides
  const overrides = {
    en: 'US',
    gb: 'GB',
    uae: 'AE',
    us: 'US',
    usa: 'US',
  };

  if (overrides[lowerCode]) return overrides[lowerCode];

  // If it's a standard 2-letter ISO code, Flagpack will handle it
  if (code.length === 2) return code.toUpperCase();

  return null;
};

const cleanText = (text) => {
  if (!text) return '';
  return String(text)
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');
};

const translations = {
  ru: {
    title: 'Все вакансии',
    filter: 'Фильтр по странам',
    clear: 'Сбросить фильтр',
    noJobs: 'Вакансии не найдены',
    back: 'На главную',
    detail: 'Подробнее',

    // Categories
    all: 'Все',
    agency: 'Агентства',
    model: 'Модели',
    operator: 'Операторы',
    scout: 'Скауты',

    // Types
    allTypes: 'Все типы',
    seeking: 'Ищу',
    offering: 'Предлагаю'
  },
  en: {
    title: 'All vacancies',
    filter: 'Filter by country',
    clear: 'Clear filter',
    noJobs: 'No jobs found',
    back: 'Back to home',
    detail: 'More',

    // Categories
    all: 'All',
    agency: 'Agencies',
    model: 'Models',
    operator: 'Operators',
    scout: 'Scouts',

    // Types
    allTypes: 'All Types',
    seeking: 'Looking For',
    offering: 'Offering'
  },
  it: {
    title: 'Tutte le offerte di lavoro',
    filter: 'Filtro per paese',
    clear: 'Cancella filtro',
    noJobs: 'Nessuna offerta trovata',
    back: 'Torna alla home',
    detail: 'More',

    // Categories
    all: 'Tutti',
    agency: 'Agenzie',
    model: 'Modelli',
    operator: 'Operatori',
    scout: 'Scout',

    // Types
    allTypes: 'Tutti',
    seeking: 'Cerco',
    offering: 'Offro'
  },
  fr: {
    title: 'Toutes les offres',
    filter: 'Filtrer par pays',
    clear: 'Effacer le filtre',
    noJobs: 'Aucune offre trouvée',
    back: 'Retour à la page d’accueil',
    detail: 'More',

    // Categories
    all: 'Tous',
    agency: 'Agences',
    model: 'Modèles',
    operator: 'Opérateurs',
    scout: 'Scouts',

    // Types
    allTypes: 'Tous',
    seeking: 'Je cherche',
    offering: 'J\'offre'
  },
};

const WorkList = ({ language }) => {
  const [jobs, setJobs] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedType, setSelectedType] = useState(null);

  // UI States
  const [isCountryModalOpen, setIsCountryModalOpen] = useState(false);
  const [countrySearchQuery, setCountrySearchQuery] = useState('');

  const location = useLocation();
  const navigate = useNavigate();

  const t = translations[language] || translations.en;

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      offset: 150,
      startEvent: 'DOMContentLoaded',
      initClassName: false,
      animatedClassName: 'aos-animate',
    });
    // Ensure AOS is ready
    AOS.refresh();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlCountry = params.get('country') || params.get('user_lang');
    const urlCategory = params.get('category');
    const urlType = params.get('type');

    if (urlCountry) {
      setSelectedCountry(urlCountry);
    } else {
      setSelectedCountry(null);
    }

    if (urlCategory) {
      setSelectedCategory(urlCategory);
    } else {
      setSelectedCategory(null);
    }

    if (urlType) {
      setSelectedType(urlType.toLowerCase());
    } else {
      setSelectedType(null);
    }

    fetchJobs(urlCountry || 'all', urlCategory, urlType);
  }, [language, location.search]);

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    // Refresh AOS when jobs are loaded to ensure animations work
    if (jobs.length > 0 && !loading) {
      setTimeout(() => {
        AOS.refresh();
      }, 100);
    }
  }, [jobs, loading]);

  const fetchJobs = async (countryCode = 'all', category = null, type = null) => {
    try {
      setLoading(true);
      setError(null);

      const options = { limit: 'all' };
      if (category) options.category = category;
      if (type) options.type = type;

      const response = await getJobs(countryCode, options);

      let jobsData = response.data;
      if (Array.isArray(jobsData)) {
        setJobs(jobsData);
      } else if (jobsData && Array.isArray(jobsData.data)) {
        setJobs(jobsData.data);
      } else if (jobsData && jobsData.jobs && Array.isArray(jobsData.jobs)) {
        setJobs(jobsData.jobs);
      } else {
        setJobs([]);
      }
    } catch (err) {
      console.error('Error fetching jobs list:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load jobs');
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await getCountries();
      setCountries(response.data || []);
    } catch (err) {
      console.error('Error fetching countries:', err);
    }
  };

  const handleCountryFilter = (code) => {
    const params = new URLSearchParams(location.search);
    if (code === null || code === selectedCountry) {
      params.delete('country');
      navigate(`/work?${params.toString()}`);
    } else {
      params.set('country', code);
      navigate(`/work?${params.toString()}`);
    }
  };

  const handleCategoryFilter = (cat) => {
    const params = new URLSearchParams(location.search);
    if (cat === null || cat === selectedCategory) {
      params.delete('category');
      setSelectedCategory(null);
    } else {
      params.set('category', cat);
      setSelectedCategory(cat);
    }
    navigate(`/work?${params.toString()}`);
  };
  const handleTypeFilter = (type) => {
    const params = new URLSearchParams(location.search);
    if (type === null || type === selectedType) {
      params.delete('type');
      setSelectedType(null);
    } else {
      params.set('type', type);
      setSelectedType(type);
    }
    navigate(`/work?${params.toString()}`);
  };


  // Categories config
  const categories = [
    { id: 'agency', label: t.agency },
    { id: 'model', label: t.model },
    { id: 'operator', label: t.operator },
    { id: 'scout', label: t.scout }
  ];

  // Types config
  const types = [
    { id: 'seeking', label: t.seeking },
    { id: 'offering', label: t.offering }
  ];

  const filteredCountries = countries.filter(c =>
    c.NAME.toLowerCase().includes(countrySearchQuery.toLowerCase())
  );

  const activeCountry = countries.find(c => c.CODE_COUNTRY === selectedCountry);


  return (
    <div className="main">
      <div className="workBlock mainpage">
        <div className="container workcont">
          <div className="row">
            <div className="title">
              <h2>{t.title}</h2>
            </div>

          </div>

          {/* Premium Filter Bar */}
          <div className="premium-filter-bar">
            {/* Row 1: Location selection */}
            <div className="filter-row">
              <button className="country-selector-btn" onClick={() => setIsCountryModalOpen(true)}>
                {activeCountry ? (
                  <>
                    <Flag code={getCountryFlagCode(activeCountry.CODE_COUNTRY)} size="S" hasDropShadow gradient="real-linear" />
                    <span>{activeCountry.NAME}</span>
                  </>
                ) : (
                  <>
                    <img src="/local/templates/kapussta/img/fil.svg" alt="all" style={{ width: '20px' }} />
                    <span>{t.filter}</span>
                  </>
                )}
                <span style={{ marginLeft: '10px', opacity: 0.5 }}>▼</span>
              </button>

              {selectedCountry && (
                <button
                  onClick={() => handleCountryFilter(null)}
                  style={{ background: 'none', border: 'none', color: '#836FFF', cursor: 'pointer', fontSize: '14px' }}
                >
                  {t.clear}
                </button>
              )}
            </div>

            {/* Row 2: Categories (Swipeable Chips) */}
            <div className="filter-pills-scroll">
              <button
                className={`filter-pill ${!selectedCategory ? 'active' : ''}`}
                onClick={() => handleCategoryFilter(null)}
              >
                {t.all}
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  className={`filter-pill ${selectedCategory === cat.id ? 'active' : ''}`}
                  onClick={() => handleCategoryFilter(cat.id)}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            {/* Row 3: Type Selector */}
            <div className="filter-pills-scroll">
              <button
                className={`filter-pill ${!selectedType ? 'active' : ''}`}
                onClick={() => handleTypeFilter(null)}
              >
                {t.allTypes}
              </button>
              {types.map(type => (
                <button
                  key={type.id}
                  className={`filter-pill ${selectedType === type.id ? 'active' : ''}`}
                  onClick={() => handleTypeFilter(type.id)}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Country Selector Modal */}
          {isCountryModalOpen && (
            <div className="country-modal-overlay" onClick={() => setIsCountryModalOpen(false)}>
              <div className="country-modal" onClick={e => e.stopPropagation()}>
                <button className="close-modal-btn" onClick={() => setIsCountryModalOpen(false)}>×</button>

                <div className="modal-header">
                  <h2>{t.filter}</h2>
                  <div className="search-wrapper">
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Поиск страны..."
                      value={countrySearchQuery}
                      onChange={e => setCountrySearchQuery(e.target.value)}
                      autoFocus
                    />
                  </div>
                </div>

                <div className="country-grid">
                  {/* Option for "All countries" */}
                  <div
                    className={`country-item ${!selectedCountry ? 'active' : ''}`}
                    onClick={() => { handleCountryFilter(null); setIsCountryModalOpen(false); }}
                  >
                    <img src="/local/templates/kapussta/img/fil.svg" alt="all" style={{ width: '20px' }} />
                    <span>Все страны</span>
                  </div>

                  {filteredCountries.map(country => (
                    <div
                      key={country.ID}
                      className={`country-item ${selectedCountry === country.CODE_COUNTRY ? 'active' : ''}`}
                      onClick={() => { handleCountryFilter(country.CODE_COUNTRY); setIsCountryModalOpen(false); }}
                    >
                      <Flag
                        code={getCountryFlagCode(country.CODE_COUNTRY)}
                        size="S"
                        hasDropShadow
                        gradient="real-linear"
                      />
                      <span>{country.NAME}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {loading && <div className="loading">Loading...</div>}
          {error && <div className="jobs-status error">{error}</div>}

          {!loading && jobs.length === 0 && (
            <div className="jobs-status">{t.noJobs}</div>
          )}

          <div className="work">
            {jobs.map((job) => (
              <div key={job.ID} className="smallWork" data-aos="fade-up" data-aos-once="true" data-aos-offset="150" data-aos-duration="1000">
                {job.PREVIEW_PICTURE_SRC && (
                  <div className="image">
                    <img src={getImageUrl(job.PREVIEW_PICTURE_SRC)} alt={job.NAME} loading="lazy" />
                  </div>
                )}
                <div className="content">
                  <div className="title">
                    <h3>{job.NAME}</h3>
                  </div>
                  {job.PREVIEW_TEXT && (
                    <p className="job-short-desc" dangerouslySetInnerHTML={{ __html: cleanText(job.PREVIEW_TEXT) }} />
                  )}
                  <div className="cost" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <img src="/local/templates/kapussta/img/doll.svg" alt="$" style={{ width: '14px', height: '14px' }} />
                    <p dangerouslySetInnerHTML={{ __html: cleanText(job.PROPERTIES?.PRICE?.VALUE || '') }} style={{ margin: 0 }} />
                  </div>
                  <div className="button">
                    <Link to={job.DETAIL_PAGE_URL?.replace(/\/$/, '') || `/work/${job.CODE}`}>{translations[language]?.detail || 'Подробнее'}</Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="btnAll" style={{ marginTop: '40px' }}>
            <Link to="/">{t.back}</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkList;

